const DNI_LETRAS = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIF_LETRAS = "JABCDEFGHI";
const PLACEHOLDER = new Set(["", "-", "\u2014", "\u2013", "?", "N/A", "NA", "S/N", "SN", "SIN", "NINGUNO", "NONE"]);

function soloAlnum(s) {
  return String(s || "").toUpperCase().replace(/[^0-9A-Z]/g, "");
}

export function letraDni(numero) {
  return DNI_LETRAS[Number(numero) % 23];
}

export function esDni(valor) {
  const m = soloAlnum(valor).match(/^(\d{8})([A-Z])$/);
  if (!m) return false;
  return m[2] === letraDni(Number(m[1]));
}

export function esNie(valor) {
  const m = soloAlnum(valor).match(/^([XYZ])(\d{7})([A-Z])$/);
  if (!m) return false;
  const map = { X: "0", Y: "1", Z: "2" };
  return m[3] === letraDni(Number(map[m[1]] + m[2]));
}

function cifControl(siete) {
  let total = 0;
  for (let i = 0; i < siete.length; i += 1) {
    let n = Number(siete[i]);
    if (i % 2 === 0) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    total += n;
  }
  const unidad = total % 10;
  const dig = unidad === 0 ? 0 : 10 - unidad;
  return { dig: String(dig), let: CIF_LETRAS[dig] };
}

export function esCif(valor) {
  const m = soloAlnum(valor).match(/^([ABCDEFGHJNPQRSUVW])(\d{7})([0-9A-J])$/);
  if (!m) return false;
  const { dig, let: letra } = cifControl(m[2]);
  return m[3] === dig || m[3] === letra;
}

export function esNif(valor) {
  const raw = soloAlnum(valor);
  if (!raw || PLACEHOLDER.has(raw)) return false;
  return esDni(raw) || esNie(raw) || esCif(raw);
}

const PAISES_VAT = new Set([
  "AD", "AT", "BE", "BG", "CH", "CY", "CZ", "DE", "DK", "EE", "EL", "ES", "FI",
  "FR", "GB", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV", "MC", "MT",
  "NL", "NO", "PL", "PT", "RO", "SE", "SI", "SK", "XI",
]);

function prefijoPaisVat(raw) {
  const s = soloAlnum(raw);
  if (s.length < 4 || !/^[A-Z]{2}/.test(s)) return { cc: "", rest: s };
  const cc = s.slice(0, 2);
  if (cc === "EL") return { cc: "GR", rest: s.slice(2) };
  if (PAISES_VAT.has(cc)) return { cc, rest: s.slice(2) };
  return { cc: "", rest: s };
}

function pareceIdentificadorEs(raw) {
  return /^\d{8}[A-Z]$/.test(raw) || /^[XYZ]\d{7}[A-Z]$/.test(raw) || /^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(raw);
}

function restoVatOk(rest) {
  if (!/^[0-9A-Z]{5,13}$/.test(rest)) return false;
  return (rest.match(/\d/g) || []).length >= 2;
}

export function esIdFiscal(valor) {
  const raw = soloAlnum(valor);
  if (!raw || PLACEHOLDER.has(raw)) return false;
  const { cc, rest } = prefijoPaisVat(raw);
  if (cc === "ES") return esNif(rest);
  if (cc) return restoVatOk(rest);
  if (esNif(raw)) return true;
  if (pareceIdentificadorEs(raw)) return false;
  if (!/^[0-9A-Z]{5,16}$/.test(raw)) return false;
  return (raw.match(/\d/g) || []).length >= 4;
}

export function esEmail(valor) {
  const raw = String(valor || "").trim();
  if (!raw || raw.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
}

export function esCp(valor) {
  const m = String(valor || "").trim().match(/^(\d{5})$/);
  if (!m) return false;
  const prov = Number(m[1].slice(0, 2));
  return prov >= 1 && prov <= 52;
}

export function esCpIntl(valor) {
  const s = String(valor || "").trim().toUpperCase();
  if (!s || PLACEHOLDER.has(s)) return false;
  if (esCp(s)) return true;
  if (/^\d{5}$/.test(s)) return s !== "00000";
  const compact = s.replace(/[\s\-]/g, "");
  if (!/^[A-Z0-9]{4,10}$/.test(compact)) return false;
  if (new Set(compact).size < 2) return false;
  return (compact.match(/\d/g) || []).length >= 3;
}

export function esIdConductor(valor) {
  const raw = soloAlnum(valor);
  if (!raw || PLACEHOLDER.has(raw)) return false;
  if (esDni(raw) || esNie(raw)) return true;
  if (/^\d{8}[A-Z]$/.test(raw) || /^[XYZ]\d{7}[A-Z]$/.test(raw)) return false;
  return esIdFiscal(raw);
}

export function esDireccionCompleta(valor) {
  const s = String(valor || "").trim();
  if (!s || PLACEHOLDER.has(s.toUpperCase())) return false;
  const m = s.match(/\b(\d{5})\b/);
  if (!m || !esCp(m[1])) return false;
  const before = s.slice(0, m.index);
  const after = s.slice(m.index + 5);
  const letras = before.match(/[A-Za-z\u00c1\u00c9\u00cd\u00d3\u00da\u00e1\u00e9\u00ed\u00f3\u00fa\u00d1\u00f1\u00dc\u00fc]/g) || [];
  if (letras.length < 5) return false;
  const palabras = after.match(/[A-Za-z\u00c1\u00c9\u00cd\u00d3\u00da\u00e1\u00e9\u00ed\u00f3\u00fa\u00d1\u00f1\u00dc\u00fc]{3,}/g) || [];
  return palabras.length >= 1;
}

function compactMat(valor) {
  return String(valor || "").toUpperCase().replace(/[\s.\-_/]/g, "");
}

export function esMatriculaEs(valor) {
  const raw = compactMat(valor);
  if (!raw || PLACEHOLDER.has(raw)) return false;
  if (/^(R)?(\d{4})([A-Z]{3})$/.test(raw)) return true;
  return /^(R)?([A-Z]{1,2})(\d{4})([A-Z]{1,2})$/.test(raw);
}

function esMatriculaExtranjera(valor) {
  const raw = compactMat(valor);
  if (!raw || PLACEHOLDER.has(raw) || esMatriculaEs(valor)) return false;
  if (!/^[A-Z0-9]{4,12}$/.test(raw)) return false;
  if (new Set(raw).size < 2) return false;
  const letras = (raw.match(/[A-Z]/g) || []).length;
  const digitos = (raw.match(/\d/g) || []).length;
  return letras >= 1 && digitos >= 2;
}

export function esMatricula(valor) {
  return esMatriculaEs(valor) || esMatriculaExtranjera(valor);
}

export function formatearMatricula(valor) {
  const raw = compactMat(valor);
  if (!raw || PLACEHOLDER.has(raw)) return "";
  let m = raw.match(/^(R)?(\d{4})([A-Z]{3})$/);
  if (m) return m[1] ? `R-${m[2]}-${m[3]}` : `${m[2]}-${m[3]}`;
  m = raw.match(/^(R)?([A-Z]{1,2})(\d{4})([A-Z]{1,2})$/);
  if (m) {
    const cuerpo = `${m[2]}-${m[3]}-${m[4]}`;
    return m[1] ? `R-${cuerpo}` : cuerpo;
  }
  if (esMatriculaExtranjera(valor)) {
    return String(valor || "").trim().toUpperCase().replace(/[\s.\-_/]+/g, "-").replace(/^-|-$/g, "");
  }
  return "";
}
